import { createElement as h } from "../utils";

import Disclosure from "./Disclosure";

export default class Sidebar extends HTMLElement {
  /**
   * Define the element in the document's custom element registry.
   *
   * @param {string} [tag] The tag to use in the element definition
   */
  static define(tag = "ifrs-sidebar") {
    if (!customElements.get(tag)) {
      customElements.define(tag, this);
    }
  }

  constructor() {
    super();
    this.cleanup = new Set();
  }

  /**
   * A collection for side effect cleanup functions. Each function in the set
   * is called and removed when the component is disconnected.
   *
   * @type {Set<() => void>}
   */
  cleanup;

  /**
   * The current state of the component.
   *
   * @type {boolean}
   */
  get open() {
    return this.hasAttribute("open");
  }

  /**
   * @param {boolean} value The new state of the component.
   */
  set open(value) {
    this.toggleAttribute("open", !!value);
  }

  /**
   * Reacts to changes of observed attributes. The function makes DOM updates
   * to reflect the current state of the component.
   *
   * @param {string} name The changed attribute name
   * @param {*} prev The previous value of the attribute
   * @param {*} next The new value of the attribute
   */
  async attributeChangedCallback(name, prev, next) {
    // Toggle component state when the `open` attribute changes.
    if (name === "open") {
      // Get the new state of the component.
      const isOpen = next !== null;

      this.querySelectorAll("ifrs-disclosure").forEach((el) => {
        if (el instanceof Disclosure) {
          el.open = isOpen;
        }
      });
    }
  }

  /**
   * Set up the component once it has been added to the DOM.
   */
  connectedCallback() {
    // Exit early if the component is not connected.
    if (!this.isConnected) {
      return;
    }

    // Set up component markup
    this.setup();

    /**
     * Listen for descendant toggle events
     *
     * @param {ToggleEvent} event
     */
    const handleToggle = (event) => {
      if (event.target instanceof Disclosure) {
        this.open = event.target.open;
      }
    };

    this.addEventListener("toggle", handleToggle);
    this.cleanup.add(() => this.removeEventListener("toggle", handleToggle));

    /**
     * Listen for resize events
     *
     * @param {MediaQueryListEvent} event
     */
    const handleMediaChange = (event) => {
      this.open = event.matches;
    };

    const mediaQuery = window.matchMedia("(min-width: 54em)");
    this.open = mediaQuery.matches;
    mediaQuery.addEventListener("change", handleMediaChange);
    this.cleanup.add(() =>
      mediaQuery.removeEventListener("change", handleMediaChange),
    );
  }

  setup() {
    if (!(this.firstElementChild instanceof Disclosure)) {
      const disclosure = /** @type {Disclosure} */ (
        h("ifrs-disclosure", {}, this.children)
      );
      this.append(disclosure);
      this.open = disclosure.open;
    }
  }

  /**
   * Clean up side effects when the component is disconnected.
   */
  disconnectedCallback() {
    // Call and delete all cleanup functions
    if (this.cleanup) {
      this.cleanup.forEach((fn) => fn());
      this.cleanup.clear();
    }
  }
}

Object.assign(Sidebar, { observedAttributes: ["open"] });
